import { useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Slider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as z from "zod";
import AddIcon from "@mui/icons-material/Add";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import type { Client } from "trailbase";
import type { Session } from "../../app/App";
import { createName, deleteName, updateName } from "../../data/api";
import {
  refreshNames,
  refreshRatings,
  useNames,
  useRatingSubscription,
  useRatings,
} from "../../data/hooks";
import { saveRating } from "../../domain/rating";
import { deriveStatus, statusRank } from "../../domain/status";
import { findSimilarNames, normalizeName, parseTsv } from "../../domain/names";
import type {
  Gender,
  NameRecord,
  NameStatus,
  NameWithRatings,
  NewName,
  RatingScore,
} from "../../domain/types";
import { statusColor, statusValues } from "../../domain/types";
import { NameFields, type NameFieldsValue } from "../shared/NameFields";
import { RatingButtons } from "../shared/RatingButtons";

const sortKeys = ["status", "name", "gender", "mine", "partner"] as const;
type SortKey = (typeof sortKeys)[number];

const sortKeySchema = z.enum(sortKeys);
const statusFilterSchema = z.enum(["all", ...statusValues] as const);
const genderFilterSchema = z.enum(["all", "boy", "girl", "neutral"] as const);

type RatingFilter = {
  readonly min: number;
  readonly max: number;
  readonly includeUnrated: boolean;
};

const ratingInRange = (
  score: RatingScore | undefined,
  filter: RatingFilter,
): boolean => {
  if (score === undefined) {
    return filter.includeUnrated;
  }
  return score >= filter.min && score <= filter.max;
};

const defaultRatingFilter: RatingFilter = {
  min: -2,
  max: 2,
  includeUnrated: true,
};

export const ManagementPage = ({
  session,
}: {
  readonly session: Session;
}): ReactElement => {
  const { client, user } = session;
  const names = useNames(client);
  const ratings = useRatings(client);
  useRatingSubscription(client);

  const [query, setQuery] = useState("");
  const [notesQuery, setNotesQuery] = useState("");
  const [gender, setGender] = useState<Gender | "all">("all");
  const [status, setStatus] = useState<NameStatus | "all">("all");
  const [mineFilter, setMineFilter] =
    useState<RatingFilter>(defaultRatingFilter);
  const [partnerFilter, setPartnerFilter] =
    useState<RatingFilter>(defaultRatingFilter);
  const [sortKey, setSortKey] = useState<SortKey>("status");
  const [sortDescending, setSortDescending] = useState(false);
  const [selected, setSelected] = useState<NameWithRatings | undefined>();
  const [importOpen, setImportOpen] = useState(false);

  const rows = useMemo(() => {
    if (names.status !== "ready" || ratings.status !== "ready") {
      return [];
    }
    const partnerId = ratings.data.find(
      (rating) => rating.user_id !== user.id,
    )?.user_id;
    const built: NameWithRatings[] = names.data.map((name) => {
      const nameRatings = ratings.data.filter(
        (rating) => rating.name_id === name.id,
      );
      const mine = nameRatings.find((rating) => rating.user_id === user.id);
      const partner = nameRatings.find(
        (rating) => rating.user_id === partnerId,
      );
      return { name, mine, partner, status: deriveStatus(mine, partner) };
    });
    const filtered = built.filter(
      (row) =>
        row.name.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()) &&
        row.name.notes
          .toLocaleLowerCase()
          .includes(notesQuery.toLocaleLowerCase()) &&
        (gender === "all" || row.name.gender === gender) &&
        (status === "all" || row.status === status) &&
        ratingInRange(row.mine?.score, mineFilter) &&
        ratingInRange(row.partner?.score, partnerFilter),
    );
    return [...filtered].sort((left, right) => {
      const comparators: Record<
        SortKey,
        (a: NameWithRatings, b: NameWithRatings) => number
      > = {
        name: (a, b) => a.name.name.localeCompare(b.name.name),
        gender: (a, b) => a.name.gender.localeCompare(b.name.gender),
        mine: (a, b) => (a.mine?.score ?? -3) - (b.mine?.score ?? -3),
        partner: (a, b) => (a.partner?.score ?? -3) - (b.partner?.score ?? -3),
        status: (a, b) =>
          statusRank[a.status] - statusRank[b.status] ||
          a.name.name.localeCompare(b.name.name),
      };
      const comparison = comparators[sortKey](left, right);
      return sortDescending ? -comparison : comparison;
    });
  }, [
    gender,
    mineFilter,
    names,
    notesQuery,
    partnerFilter,
    query,
    ratings,
    sortDescending,
    sortKey,
    status,
    user.id,
  ]);

  if (names.status === "error" || ratings.status === "error") {
    const error =
      names.status === "error"
        ? names.error
        : ratings.status === "error"
          ? ratings.error
          : new Error();
    return (
      <Alert severity="error">Unable to load names: {error.message}</Alert>
    );
  }
  if (names.status === "loading" || ratings.status === "loading") {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalNames = names.data.length;
  const closeSelected = (): void => setSelected(undefined);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Your shortlist
          </Typography>
          <Typography color="text.secondary">
            {totalNames} names, shared between you both.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => setImportOpen(true)}
          >
            Paste TSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              setSelected({ name: emptyName(user.id), status: "Pending" })
            }
          >
            Add name
          </Button>
        </Stack>
      </Box>
      <FilterBar
        query={query}
        notesQuery={notesQuery}
        gender={gender}
        status={status}
        mineFilter={mineFilter}
        partnerFilter={partnerFilter}
        sortKey={sortKey}
        sortDescending={sortDescending}
        onQuery={setQuery}
        onNotesQuery={setNotesQuery}
        onGender={setGender}
        onStatus={setStatus}
        onMineFilter={setMineFilter}
        onPartnerFilter={setPartnerFilter}
        onSortKey={setSortKey}
        onSortDescending={setSortDescending}
      />
      {rows.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: "center" }}>
          <Typography variant="h6">No names match these filters.</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Add a name or paste a TSV list to get started.
          </Typography>
        </Paper>
      ) : (
        <NameTable rows={rows} onSelect={setSelected} />
      )}
      <DetailDialog
        key={selected?.name.id ?? "none"}
        session={session}
        row={selected}
        onClose={closeSelected}
      />
      <ImportDialog
        client={client}
        open={importOpen}
        existingNames={names.data.map((name) => name.name)}
        onClose={() => setImportOpen(false)}
      />
    </Stack>
  );
};

const emptyName = (userId: string): NameRecord => ({
  id: "new",
  name: "",
  gender: "neutral",
  notes: "",
  created_by: userId,
  created_at: 0,
});

type FilterBarProps = {
  readonly query: string;
  readonly notesQuery: string;
  readonly gender: Gender | "all";
  readonly status: NameStatus | "all";
  readonly mineFilter: RatingFilter;
  readonly partnerFilter: RatingFilter;
  readonly sortKey: SortKey;
  readonly sortDescending: boolean;
  readonly onQuery: (value: string) => void;
  readonly onNotesQuery: (value: string) => void;
  readonly onGender: (value: Gender | "all") => void;
  readonly onStatus: (value: NameStatus | "all") => void;
  readonly onMineFilter: (value: RatingFilter) => void;
  readonly onPartnerFilter: (value: RatingFilter) => void;
  readonly onSortKey: (value: SortKey) => void;
  readonly onSortDescending: (value: boolean) => void;
};

const FilterBar = (props: FilterBarProps): ReactElement => (
  <Paper sx={{ p: 2 }}>
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      sx={{ flexWrap: "wrap", alignItems: "center" }}
    >
      <TextField
        size="small"
        label="Search names"
        value={props.query}
        onChange={(e) => props.onQuery(e.target.value)}
      />
      <TextField
        size="small"
        label="Search notes"
        value={props.notesQuery}
        onChange={(e) => props.onNotesQuery(e.target.value)}
      />
      <LabeledSelect
        label="Gender"
        value={props.gender}
        onChange={(v) => props.onGender(genderFilterSchema.parse(v))}
        options={["all", "boy", "girl", "neutral"]}
      />
      <LabeledSelect
        label="Status"
        value={props.status}
        onChange={(v) => props.onStatus(statusFilterSchema.parse(v))}
        options={["all", ...statusValues]}
      />
      <RatingRangeField
        label="My rating"
        filter={props.mineFilter}
        onChange={props.onMineFilter}
      />
      <RatingRangeField
        label="Partner rating"
        filter={props.partnerFilter}
        onChange={props.onPartnerFilter}
      />
      <LabeledSelect
        label="Sort"
        value={props.sortKey}
        onChange={(v) => props.onSortKey(sortKeySchema.parse(v))}
        options={sortKeys}
      />
      <Button onClick={() => props.onSortDescending(!props.sortDescending)}>
        {props.sortDescending ? "Descending" : "Ascending"}
      </Button>
    </Stack>
  </Paper>
);

type LabeledSelectProps = {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly string[];
};

const LabeledSelect = ({
  label,
  value,
  onChange,
  options,
}: LabeledSelectProps): ReactElement => (
  <FormControl sx={{ minWidth: 130 }}>
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option === "all" ? "All" : option}
        </MenuItem>
      ))}
    </TextField>
  </FormControl>
);

const RatingRangeField = ({
  label,
  filter,
  onChange,
}: {
  readonly label: string;
  readonly filter: RatingFilter;
  readonly onChange: (next: RatingFilter) => void;
}): ReactElement => (
  <Stack spacing={0.5} sx={{ minWidth: 180 }}>
    <Typography variant="caption" color="text.secondary">
      {label}: {filter.min} to {filter.max}
    </Typography>
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Box sx={{ flex: 1 }}>
        <Slider
          value={[filter.min, filter.max]}
          min={-2}
          max={2}
          step={1}
          onChange={(_, value) => {
            if (Array.isArray(value)) {
              const [min, max] = value;
              if (min !== undefined && max !== undefined) {
                onChange({ ...filter, min, max });
              }
            }
          }}
        />
      </Box>
    </Stack>
    <FormControlLabel
      control={
        <Switch
          checked={filter.includeUnrated}
          onChange={(e) =>
            onChange({ ...filter, includeUnrated: e.target.checked })
          }
        />
      }
      label="Include unrated"
    />
  </Stack>
);

const NameTable = ({
  rows,
  onSelect,
}: {
  readonly rows: readonly NameWithRatings[];
  readonly onSelect: (row: NameWithRatings) => void;
}): ReactElement => (
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Gender</TableCell>
          <TableCell>Notes</TableCell>
          <TableCell>My rating</TableCell>
          <TableCell>Partner</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            hover
            key={row.name.id}
            onClick={() => onSelect(row)}
            sx={{ cursor: "pointer" }}
          >
            <TableCell sx={{ fontWeight: 600 }}>{row.name.name}</TableCell>
            <TableCell>{row.name.gender}</TableCell>
            <TableCell
              sx={{
                maxWidth: 280,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.name.notes === "" ? "—" : row.name.notes}
            </TableCell>
            <TableCell>{row.mine?.score ?? "—"}</TableCell>
            <TableCell>{row.partner?.score ?? "—"}</TableCell>
            <TableCell>
              <Chip
                size="small"
                label={row.status}
                color={statusColor[row.status]}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const DetailDialog = ({
  session,
  row,
  onClose,
}: {
  readonly session: Session;
  readonly row: NameWithRatings | undefined;
  readonly onClose: () => void;
}): ReactElement => {
  const { client, user } = session;
  const isNew = row?.name.id === "new";
  const existing = row?.name ?? undefined;
  const [fields, setFields] = useState<NameFieldsValue>(
    existing === undefined
      ? { name: "", gender: "neutral", notes: "" }
      : { name: existing.name, gender: existing.gender, notes: existing.notes },
  );
  const [score, setScore] = useState<RatingScore | undefined>(row?.mine?.score);
  const [reason, setReason] = useState(row?.mine?.reason ?? "");
  const [error, setError] = useState<string | undefined>();
  const [confirmingSimilar, setConfirmingSimilar] = useState<
    readonly string[] | undefined
  >();

  if (row === undefined) {
    return <></>;
  }

  const saveAll = async (similarOverride: boolean): Promise<void> => {
    setError(undefined);
    const trimmedName = fields.name.trim();
    if (trimmedName === "") {
      setError("Name is required.");
      return;
    }
    const input: NewName = {
      name: trimmedName,
      gender: fields.gender,
      notes: fields.notes.trim(),
    };

    if (isNew && !similarOverride) {
      const allNames = await listNamesForDedup(client);
      const normalizedNew = normalizeName(trimmedName);
      const isExactDuplicate = allNames.some(
        (existing) => normalizeName(existing) === normalizedNew,
      );
      if (isExactDuplicate) {
        setError(`"${trimmedName}" already exists.`);
        return;
      }
      const similar = findSimilarNames(trimmedName, allNames);
      if (similar.length > 0) {
        setConfirmingSimilar(similar);
        return;
      }
    }

    try {
      if (isNew) {
        await createName(client, input);
      } else {
        await updateName(client, row.name.id, input);
      }
      await refreshNames(client);
      if (score !== undefined && !isNew) {
        await saveRating(
          client,
          row.name.id,
          user.id,
          score,
          reason.trim(),
          row.mine,
        );
        await refreshRatings(client);
      }
      onClose();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Unable to save.");
    }
  };

  const remove = async (): Promise<void> => {
    if (isNew || row === undefined) {
      onClose();
      return;
    }
    if (window.confirm(`Delete ${row.name.name}?`)) {
      try {
        await deleteName(client, row.name.id);
        await refreshNames(client);
        onClose();
      } catch (cause: unknown) {
        setError(cause instanceof Error ? cause.message : "Unable to delete.");
      }
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isNew ? "Add a name" : row.name.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error !== undefined && <Alert severity="error">{error}</Alert>}
          {confirmingSimilar !== undefined && (
            <Alert severity="warning">
              Similar existing names: {confirmingSimilar.join(", ")}. Add
              anyway?
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setConfirmingSimilar(undefined);
                    void saveAll(true);
                  }}
                >
                  Add anyway
                </Button>
                <Button
                  size="small"
                  onClick={() => setConfirmingSimilar(undefined)}
                >
                  Cancel
                </Button>
              </Box>
            </Alert>
          )}
          <NameFields value={fields} onChange={setFields} />
          {!isNew && (
            <>
              <RatingButtons value={score} onChange={setScore} />
              <TextField
                label="Reason"
                value={reason}
                multiline
                minRows={2}
                onChange={(e) => setReason(e.target.value)}
              />
              <Typography variant="body2" color="text.secondary">
                Partner:{" "}
                {row.partner === undefined
                  ? "not rated"
                  : `${row.partner.score}${row.partner.reason === "" ? "" : ` — ${row.partner.reason}`}`}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {!isNew && (
          <Button color="error" onClick={() => void remove()}>
            Delete
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void saveAll(false)}
          disabled={fields.name.trim() === ""}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const listNamesForDedup = async (
  client: Client,
): Promise<readonly string[]> => {
  const api = client.records<{ name: string }>("names");
  const names: string[] = [];
  let cursor: string | undefined;
  do {
    const pagination =
      cursor === undefined ? { limit: 1024 } : { limit: 1024, cursor };
    const response = await api.list({ pagination });
    for (const record of response.records) {
      names.push(record.name);
    }
    cursor = response.cursor;
  } while (cursor !== undefined && cursor !== "");
  return names;
};

const ImportDialog = ({
  client,
  open,
  existingNames,
  onClose,
}: {
  readonly client: Client;
  readonly open: boolean;
  readonly existingNames: readonly string[];
  readonly onClose: () => void;
}): ReactElement => {
  const [value, setValue] = useState("");
  const [message, setMessage] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  if (!open) {
    return <></>;
  }

  const submit = async (): Promise<void> => {
    setError(undefined);
    setMessage(undefined);
    const parsed = parseTsv(value);
    if (parsed.errors.length > 0) {
      setError(parsed.errors.join("; "));
      return;
    }
    const normalized = new Set(existingNames.map(normalizeName));
    const candidates = parsed.rows.filter((row) => {
      const normalizedRow = normalizeName(row.name);
      if (normalized.has(normalizedRow)) {
        return false;
      }
      normalized.add(normalizedRow);
      return true;
    });
    const similar = candidates.flatMap((row) =>
      findSimilarNames(row.name, existingNames).map(
        (s) => `${row.name} ≈ ${s}`,
      ),
    );
    if (
      similar.length > 0 &&
      !window.confirm(
        `Possible similar existing names:\n${similar.join("\n")}\n\nImport anyway?`,
      )
    ) {
      return;
    }
    try {
      await Promise.all(candidates.map((row) => createName(client, row)));
      await refreshNames(client);
      setMessage(`Imported ${candidates.length} names.`);
      setValue("");
    } catch (cause: unknown) {
      setError(
        cause instanceof Error ? cause.message : "Unable to import names.",
      );
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Paste names from TSV</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error !== undefined && <Alert severity="error">{error}</Alert>}
          {message !== undefined && <Alert severity="success">{message}</Alert>}
          <Typography variant="body2" color="text.secondary">
            One row per name: <code>name\tgender\tnotes</code>. Gender and notes
            are optional; do not include a header.
          </Typography>
          <TextField
            multiline
            minRows={10}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={"Liam\tboy\tIrish origin\nSage\tneutral\t"}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={value.trim() === ""}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};
